import { CapaDeModal } from '@/shared/components/CapaDeModal'
import { Checkbox } from '@/shared/components/Checkbox'
import SelectBuscable from '@/shared/components/SelectBuscable'
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Eye,
  EyeOff,
  AlertCircle,
  CheckIcon,
  CircleCheckBig,
  Loader2,
} from "lucide-react";
import ProviderLoginImg from '@/assets/provider-login.webp';
import bigLogo from '@/assets/big-logo.svg';
import CheckProviderStep3 from '@/assets/Step3-provider.svg';
import {
  getInvitationByToken,
  registerByInvitation,
  type InvitationData,
} from '@/features/proveedores/api/supplierInvitationService';
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton';

const CORPORATE_RFC_REGEX = /^[A-ZÑ&]{3}\d{6}[A-Z0-9]{3}$/;
const INDIVIDUAL_RFC_REGEX = /^[A-ZÑ&]{4}\d{6}[A-Z0-9]{3}$/;
const FOREIGN_RFC_REGEX = /^XEXX\d{6}[A-Z0-9]{3}$/;

const inferPersonTypeFromRfc = (
  rfc?: string
): "corporate" | "individual" | "foreign" | null => {
  const normalizedRfc = (rfc || "").trim().toUpperCase();
  if (FOREIGN_RFC_REGEX.test(normalizedRfc)) return "foreign";
  if (CORPORATE_RFC_REGEX.test(normalizedRfc)) return "corporate";
  if (INDIVIDUAL_RFC_REGEX.test(normalizedRfc)) return "individual";
  return null;
};

const RegisterProviderByInvitation = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [token, setToken] = useState<string | null>(null);
  const [invitationData, setInvitationData] = useState<InvitationData | null>(null);
  const [isLoadingInvitation, setIsLoadingInvitation] = useState(true);
  const [invitationError, setInvitationError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
    personType: "corporate",
    mainContactName: "",
    mainContactPhone: "",
    salesEmail: "",
    accountsPayableEmail: "",
    internalRequesterName: "",
    acceptTerms: false,
    country: "México",
    state: "",
    city: "",
    postalCode: "",
  });

  const [localError, setLocalError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [passwordValidation, setPasswordValidation] = useState({
    minLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false,
    hasSpecialChar: false,
  });

  const steps = [
    { id: 1, name: "Datos de cuenta" },
    { id: 2, name: "Contraseña" },
    { id: 3, name: "Confirmación" },
  ];

  useEffect(() => {
    const tokenFromUrl = searchParams.get('token');

    if (!tokenFromUrl) {
      setInvitationError('Token no proporcionado en la URL');
      setIsLoadingInvitation(false);
      return;
    }

    setToken(tokenFromUrl);
    validateToken(tokenFromUrl);
  }, [searchParams]);

  const validateToken = async (tokenValue: string) => {
    try {
      const data = await getInvitationByToken(tokenValue);
      setInvitationData(data);
      const inferredPersonType = inferPersonTypeFromRfc(data.rfc);

      // Pre-llenar campos sugeridos
      setFormData(prev => ({
        ...prev,
        personType:
          (data.personType as "corporate" | "individual" | "foreign") ||
          inferredPersonType ||
          'corporate',
        mainContactPhone: data.contactPhone,
        salesEmail: data.email,
        accountsPayableEmail: data.email,
      }));
    } catch (errorCrudo: unknown) {
      const error = errorCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      setInvitationError(error.message || 'Token inválido o expirado');
    } finally {
      setIsLoadingInvitation(false);
    }
  };

  const validatePassword = (password: string) => {
    setPasswordValidation({
      minLength: password.length >= 8,
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    if (name === 'password') {
      validatePassword(value);
    }
    if (localError) setLocalError(null);
    if (successMessage) setSuccessMessage(null);
  };

  const handleSubmit = async () => {
    if (currentStep === 2) {
      const isPasswordValid = Object.values(passwordValidation).every(v => v);
      if (!isPasswordValid) {
        setLocalError("La contraseña no cumple con todos los requisitos");
        return;
      }
      if (formData.password !== formData.confirmPassword) {
        setLocalError("Las contraseñas no coinciden");
        return;
      }
      if (!formData.acceptTerms) {
        setLocalError("Debes aceptar los términos y condiciones");
        return;
      }
      if (!token) {
        setLocalError("Token no disponible");
        return;
      }

      setIsLoading(true);
      setLocalError(null);

      try {
        const inferredPersonType = inferPersonTypeFromRfc(invitationData?.rfc);
        const registerData = {
          token,
          password: formData.password,
          personType: inferredPersonType || formData.personType,
          vatType: "General",
          mainContactName: formData.mainContactName,
          mainContactPhone: formData.mainContactPhone,
          salesEmail: formData.salesEmail,
          accountsPayableEmail: formData.accountsPayableEmail,
          internalRequesterName: formData.internalRequesterName,
          acceptTerms: formData.acceptTerms,
          country: formData.country,
          state: formData.state,
          city: formData.city,
          postalCode: formData.postalCode,
        };

        const response = await registerByInvitation(registerData);
        setSuccessMessage(response.message);
        setCurrentStep(3);
      } catch (errCrudo: unknown) {
        const err = errCrudo as {
          message?: string;
          statusCode?: number;
          status?: number;
          error?: string;
          response?: { status?: number; data?: { message?: string } };
        };
        setLocalError(err.message || "Error al registrar el proveedor");
      } finally {
        setIsLoading(false);
      }
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const displayError = invitationError || localError;

  if (isLoadingInvitation) {
    return (
      <div className="h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#315FB9] mx-auto mb-4" />
          <p className="text-gray-600">Validando invitación...</p>
        </div>
      </div>
    );
  }

  if (invitationError) {
    return (
      <div className="h-screen bg-white flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Invitación Inválida</h2>
          <p className="text-gray-600 mb-6">{invitationError}</p>
          <button
            onClick={() => navigate('/proveedores/login')}
            className={estiloDeBoton('primario')}
          >
            Ir al inicio de sesión
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-white flex overflow-hidden">
      {/* Left Side - Form Section */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-8">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="mb-8 flex flex-col items-center">
            <img src={bigLogo} alt="SLYRSA Logo" className="h-14 w-auto mb-2" />
          </div>

          {/* Welcome Text */}
          <div className="mb-6 text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Crea tu cuenta
            </h1>
            <p className="text-sm text-gray-600 text-left">
              Llena el siguiente formulario con los datos solicitados para crear
              tu cuenta.
            </p>
          </div>

          {/* Stepper Indicator */}
          <div className="flex items-start justify-center my-8">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-start">
                <div className="w-[110px] flex flex-col items-center">
                  <div
                    className={`w-[32px] h-[32px] rounded-full border-2 flex items-center justify-center transition-all
              ${step.id == currentStep
                ? "border-[#315FB9] bg-blue-600/10"
                : step.id < currentStep
                  ? "border-[#315FB9] bg-[#315FB9]"
                  : "border-gray-300 bg-white"
              }`}
                  >
                    {step.id == currentStep && (
                      <div className="w-[10px] h-[10px] bg-[#315FB9] rounded-full" />
                    )}
                    {step.id < currentStep && (
                      <CheckIcon className="w-[20px] h-[20px] bg-[#315FB9] rounded-full text-white" />
                    )}
                  </div>
                  <p className="mt-2 text-[11px] text-gray-600 text-center leading-tight">
                    {step.name}
                  </p>
                </div>

                {index < steps.length - 1 && (
                  <div
                    className={`w-[48px] h-[2px] mt-[16px] transition-all ${step.id < currentStep ? "bg-[#315FB9]" : "bg-gray-300"
                      }`}
                  />
                )}
              </div>
            ))}
          </div>

          {/* Error Message */}
          {displayError && !successMessage && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
                <p className="ml-3 text-sm text-red-800">{displayError}</p>
              </div>
            </div>
          )}

          {/*Step Form 1 */}
          {currentStep == 1 && (
            <main className="overflow-y-auto h-auto xl:max-h-[380px] 2xl:max-h-[630px]">
              {/* Form */}
              <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} className="space-y-4">
                {/* RFC Field */}
                <div>
                  <label
                    htmlFor="rfc"
                    className="block text-sm font-medium text-gray-900 mb-2"
                  >
                    RFC*
                  </label>
                  <input
                    id="rfc"
                    name="rfc"
                    type="text"
                    disabled
                    value={invitationData?.rfc || ''}
                    className="w-full px-4 py-2.5 border-0  rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed text-sm uppercase"
                  />
                </div>

                {/* Razon social Field */}
                <div>
                  <label
                    htmlFor="razonSocial"
                    className="block text-sm font-medium text-gray-900 mb-2"
                  >
                    Razón Social*
                  </label>
                  <input
                    id="razonSocial"
                    name="razonSocial"
                    type="text"
                    disabled
                    value={invitationData?.legalName || ''}
                    className="w-full px-4 py-2.5 border-0  rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed text-sm"
                  />
                </div>

                {/* Email Field */}
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-900 mb-2"
                  >
                    Correo electrónico*
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    disabled
                    value={invitationData?.email || ''}
                    className="w-full px-4 py-2.5 border-0  rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed text-sm"
                  />
                </div>
                {/* Phone Field */}
                <div>
                  <label
                    htmlFor="phone"
                    className="block text-sm font-medium text-gray-900 mb-2"
                  >
                    Teléfono*
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    disabled
                    value={invitationData?.contactPhone || ''}
                    className="w-full px-4 py-2.5 border-0  rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed text-sm"
                  />
                </div>

                {/* Provider Type Field */}
                <div>
                  <label
                    htmlFor="providerType"
                    className="block text-sm font-medium text-gray-900 mb-2"
                  >
                    Tipo de Proveedor
                  </label>
                  <SelectBuscable
                    variante="formulario"
                    id="providerType"
                    name="personType"
                    value={formData.personType}
                    onChange={(valor) => setFormData({ ...formData, personType: valor })}
                    opciones={[
                      { value: 'corporate', label: 'Persona Moral' },
                      { value: 'individual', label: 'Persona Física' },
                      { value: 'foreign', label: 'Extranjero' },
                    ]}
                    canDelete={false}
                    placeholderDeBusqueda="Buscar tipo de proveedor..."
                  />
                </div>

                {/* Service Type Field */}
                <div>
                  <label
                    htmlFor="serviceType"
                    className="block text-sm font-medium text-gray-900 mb-2"
                  >
                    Tipo de Servicio
                  </label>
                  <SelectBuscable
                    variante="formulario"
                    id="serviceType"
                    name="serviceType"
                    disabled
                    value={String(invitationData?.serviceTypeId || '1')}
                    onChange={() => undefined}
                    opciones={[
                      { value: '1', label: 'Servicios Generales' },
                      { value: '2', label: 'Tecnología' },
                      { value: '3', label: 'Construcción' },
                    ]}
                    canDelete={false}
                    placeholderDeBusqueda="Buscar tipo de servicio..."
                  />
                </div>

                {/* Next step Button */}
                <button
                  type="button"
                  disabled={isLoading}
                  className={estiloDeBoton('primario', 'w-full my-8')}
                  onClick={() => setCurrentStep(currentStep + 1)}
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                      Enviando datos...
                    </div>
                  ) : (
                    "Siguiente"
                  )}
                </button>
              </form>
            </main>
          )}

          {/* Step Form 2 */}
          {currentStep == 2 && (
            <main className="overflow-y-auto h-auto xl:max-h-[380px] 2xl:max-h-[630px]">
              {/* Form */}
              <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} className="space-y-4">
                {/* Password Field */}
                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-gray-900 mb-2"
                  >
                    Contraseña*
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      autoComplete="current-password"
                      required
                      value={formData.password}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 border-0  rounded-lg bg-[#F6F9FB] focus:bg-[#F7F9FC] focus:border-[#315FB9] focus:ring-0 transition-all duration-200 text-sm text-gray-900 placeholder-gray-400"
                      placeholder="••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {showPassword ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Password Rules */}
                <div className="text-sm mx-6 flex flex-wrap justify-between max-w-[380px] space-y-2">
                  <p className="flex items-center">
                    <CircleCheckBig className={`w-[16px] h-[16px] mr-2 ${passwordValidation.minLength ? 'text-[#1FD361]' : 'text-gray-400'}`} />
                    <span className={passwordValidation.minLength ? 'text-gray-900' : 'text-gray-600'}>Al menos 8 caracteres</span>
                  </p>
                  <p className="flex items-center">
                    <CircleCheckBig className={`w-[16px] h-[16px] mr-2 ${passwordValidation.hasUpperCase ? 'text-[#1FD361]' : 'text-gray-400'}`} />
                    <span className={passwordValidation.hasUpperCase ? 'text-gray-900' : 'text-gray-600'}>Letra mayúscula</span>
                  </p>
                  <p className="flex items-center">
                    <CircleCheckBig className={`w-[16px] h-[16px] mr-2 ${passwordValidation.hasLowerCase ? 'text-[#1FD361]' : 'text-gray-400'}`} />
                    <span className={passwordValidation.hasLowerCase ? 'text-gray-900' : 'text-gray-600'}>Letra minúscula</span>
                  </p>
                  <p className="flex items-center">
                    <CircleCheckBig className={`w-[16px] h-[16px] mr-2 ${passwordValidation.hasNumber ? 'text-[#1FD361]' : 'text-gray-400'}`} />
                    <span className={passwordValidation.hasNumber ? 'text-gray-900' : 'text-gray-600'}>Número</span>
                  </p>
                  <p className="flex items-center">
                    <CircleCheckBig className={`w-[16px] h-[16px] mr-2 ${passwordValidation.hasSpecialChar ? 'text-[#1FD361]' : 'text-gray-400'}`} />
                    <span className={passwordValidation.hasSpecialChar ? 'text-gray-900' : 'text-gray-600'}>Caracter especial</span>
                  </p>
                </div>

                {/* Confirm Password Field */}
                <div>
                  <label
                    htmlFor="confirmPassword"
                    className="block text-sm font-medium text-gray-900 mb-2"
                  >
                    Confirmar Contraseña*
                  </label>
                  <div className="relative">
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      autoComplete="current-password"
                      required
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 border-0  rounded-lg bg-[#F6F9FB] focus:bg-[#F7F9FC] focus:border-[#315FB9] focus:ring-0 transition-all duration-200 text-sm text-gray-900 placeholder-gray-400"
                      placeholder="••••••"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Remember Me */}
                <div className="flex items-center text-sm space-x-2">
                  <Checkbox
                    id="acceptTerms"
                    name="acceptTerms"
                    checked={formData.acceptTerms}
                    onChange={(e) => setFormData({ ...formData, acceptTerms: e.target.checked })}
                  />
                  <label
                    htmlFor="acceptTerms"
                    className="ml-2 block text-gray-700"
                  >
                    Acepto términos y condiciones
                  </label>
                  <button
                    type="button"
                    className="text-[#3B5998] hover:text-[#344e86] underline"
                    onClick={() => setShowTermsModal(true)}
                  >
                    (consultar)
                  </button>
                </div>

                {/* Next step Button */}
                <button
                  type="button"
                  disabled={isLoading}
                  className={estiloDeBoton('primario', 'w-full my-8')}
                  onClick={handleSubmit}
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                      Enviando datos...
                    </div>
                  ) : (
                    "Siguiente"
                  )}
                </button>
              </form>
            </main>
          )}

          {/* Step Form 3 */}
          {currentStep == 3 && (
            <main className="overflow-y-auto h-auto flex flex-col justify-center items-center">
              <div className="my-10 flex flex-col items-center justify-center">
                <img
                  src={CheckProviderStep3}
                  alt="Proveedor Registrado"
                  className=" w-[137px] h-auto"
                />
                <p className="w-[280px] text-center mt-6">
                  Revisa tu correo y utiliza el enlace enviado para iniciar
                  sesión.
                </p>
              </div>

              {/* Next step Button */}
              <button
                type="button"
                disabled={isLoading}
                className={estiloDeBoton('primario', 'w-full my-2')}
                onClick={() => navigate('/proveedores/login')}
              >
                Ir al inicio de sesión
              </button>
            </main>
          )}

          {/* Login Link */}
          <div className="mt-4 flex justify-center space-x-2">
            <p className="text-sm hover:text-[#344e86] transition-colors">
              ¿Ya tienes cuenta?
            </p>
            <button
              onClick={() => navigate('/proveedores/login')}
              className="text-sm text-[#3B5998] hover:text-[#344e86] transition-colors"
            >
              Inicia sesión aquí
            </button>
          </div>
        </div>
      </div>

      {/* Right Side - Features Section */}
      <div className="hidden lg:flex lg:w-1/2 items-center justify-center p-6 bg-gray-50">
        <div className="rounded-[32px] w-full h-[calc(100vh-3rem)] flex flex-col items-center relative overflow-hidden shadow-xl">
          <div className="relative z-30 w-full max-w-lg space-y-4">
            <div className="absolute top-5 sm:right-[20px] xl:right-[-50px] 2xl:right-[-180px] bg-white w-[154px] h-[35px] rounded-full flex justify-center items-center">
              <p className="font-semibold">Proveedor</p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-[500px] px-7 py-4 flex items-start space-x-3 absolute top-[130px] z-30 mt-12">
              <div className="flex-shrink-0 mt-0.5">
                <div className="w-4 h-4 bg-green-500 rounded-md flex items-center justify-center">
                  <svg
                    className="w-3 h-3 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
              </div>
              <p className="text-white text-sm leading-relaxed">
                Administra, monitorea y da seguimiento a tus proveedores en un
                solo sistema.
              </p>
            </div>
          </div>

          <div className="relative z-10 w-full h-full">
            <div className="absolute inset-0 rounded-[32px] blur-2xl pointer-events-none bg-gradient-to-t from-[#1FB7EE]/10 to-[#315FB9]/80"></div>
            <img
              src={ProviderLoginImg}
              alt="Login Proveedor"
              className="w-full h-auto object-center"
            />
          </div>
        </div>
      </div>

      {showTermsModal && (
        <CapaDeModal>
          <div className="w-full max-w-2xl rounded-xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b px-6 py-4">
              <h2 className="text-lg font-semibold text-gray-900">
                Términos y condiciones para proveedores
              </h2>
              <button
                type="button"
                className={estiloDeBoton('secundario')}
                onClick={() => setShowTermsModal(false)}
              >
                Cerrar
              </button>
            </div>
            <div className="max-h-[70vh] overflow-y-auto px-6 py-4 text-sm text-gray-700 space-y-4">
              <p>
                Al registrarte como proveedor en SLYRSA aceptas que la
                información proporcionada será utilizada para validar tu alta y
                gestionar la relación comercial.
              </p>
              <p>
                Debes proporcionar datos verídicos y mantenerlos actualizados.
                SLYRSA podrá rechazar o suspender registros con información
                incompleta, falsa o inconsistente.
              </p>
              <p>
                El uso de la plataforma implica aceptar las políticas internas
                de cumplimiento, auditoría y seguridad de la información.
              </p>
              <p>
                Tus datos serán tratados conforme a la normativa aplicable en
                materia de protección de datos personales.
              </p>
              <p className="text-xs text-gray-500">
                Nota: contenido temporal para frontend. Pendiente conectar con
                versión oficial publicada por backend.
              </p>
            </div>
            <div className="flex justify-end border-t px-6 py-4">
              <button
                type="button"
                className={estiloDeBoton('primario')}
                onClick={() => setShowTermsModal(false)}
              >
                Entendido
              </button>
            </div>
          </div>
        </CapaDeModal>
      )}
    </div>
  );
};

export default RegisterProviderByInvitation;
